import { AuthPaths, RoleEnum, RouteEnum } from "../common/models/enums";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  getRolePath,
  isLocalEnvironment,
} from "../common/helpers/userLinkHelper";

import SafetyEventsPage from "../pages/telemetry/models/safety/SafetyEventsPage";
import AddModelPage from "../pages/devices/components/AddModelPage";

import AddNewClientPage from "../leak-finder/components/AddNewClientPage";
import { AnyRouteHandler } from "./AnyRouteHandler";
import AzureIntegrationPage from "../pages/integrations/components/AzureIntegrationPage";
import ClientUploadsPage from "../leak-finder/ClientUploadsPage";
import CreateAlertPage from "../pages/customer-settings/tabs/alert-management/create-alert/CreateAlertPage";
import CreateLocationPage from "../pages/customer-settings/tabs/locations/CreateLocationPage";
import CreateNodePage from "../pages/nodes/nodes-create/CreateNodePage";
import CreatePlateNumberPage from "../pages/system-settings/tabs/plates-management/components/CreatePlateNumberPage";
import CreatePlateNumberPageSystemSettings from "../pages/system-settings/tabs/plates-management/components/CreatePlateNumberPageSystemSettings";
import CreateZonePage from "../pages/devices/zones-setup-page/CreateZonePage";
import CustomerSettingsPage from "../pages/customer-settings/CustomerSettingsPage";
import DataHubDashboard from "../pages/data-hub/components/DataHubDashboard";
import DataHubLiveViewPageContent from "../pages/data-hub/components/liveview/DataHubLiveViewPageContent";
import DataHubPage from "../pages/data-hub/DataHubPage";
import DataHubPageContent from "../pages/data-hub/components/DataHubPageContent";
import DeploymentHealthPage from "../pages/deployment-health/DeploymentHealthPage";
import SystemHealthPage from "../pages/system-health/SystemHealthPage";
import DeviceDetailPage from "../pages/devices/device-details/DeviceDetailPage";
import DevicesPage from "../pages/devices/DevicesPage";
import EdgeIntegrationPage from "../pages/integrations/components/EdgeIntegrationPage";
import EditCategoryPage from "../pages/system-settings/tabs/plates-management/components/plate-category/EditCategoryPage";
import EditClientPage from "../leak-finder/components/EditClientPage";
import EditDevicePage from "../pages/devices/EditDevicePage";
import EditEventDetailsPage from "../pages/recent-activities/edit-event-details-page/EditEventDetailsPage";
import EditGroupPage from "../leak-finder/components/EditGroupPage";
import EditLocationPage from "../pages/customer-settings/tabs/locations/EditLocationPage";
import EditPlateNumberPage from "../pages/system-settings/tabs/plates-management/components/EditPlateNumberPage";
import EditZonePage from "../pages/devices/components/EditZonePage";
import EmailNotificationsPage from "../pages/email-notifications/EmailNotificationsPage";
import FullPageLoader from "../common/components/item/FullPageLoader";
import HumanValidatorPage from "../pages/human-validator/HumanValidatorPage";
import LeakFinderRoleBasedRoute from "./LeakFinderRoleBasedRoute";
import LeakFinderUserPreferencesPage from "../leak-finder/pages/user-preferences/LeakFinderUserPreferencesPage";
import MediaViewerPage from "../pages/media-viewer/MediaViewerPage";
import MinervaPageRedirect from "../minerva/MinervaPageRedirect";
import ModelManagerPage from "../pages/model-manager/ModelManagerPage";
import NodesPage from "../pages/nodes/NodesPage";
import NotAuthorizedPage from "../pages/not-authorized-page/NotAuthorizedPage";
import OverviewModelPage from "../pages/devices/components/OverviewModelPage";
import PauseAlertsPage from "../pages/system-settings/tabs/plates-management/components/PauseAlertsPage";
import PlatesManagement from "../pages/system-settings/tabs/plates-management/PlatesManagement";
import RecentActivitiesPage from "../pages/recent-activities/RecentActivitiesPage";
import RoleBasedRoute from "./RoleBasedRoute";
import S3IntegrationPage from "../pages/integrations/components/S3IntegrationPage";
import StatisticsPage from "../pages/statistics/StatisticsPage";
import { Suspense } from "react";
import SystemSettingsPage from "../pages/system-settings/SystemSettingsPage";
import TelemetryPage from "../pages/telemetry/TelemetryPage";
import { UploadVideoGroupPage } from "../leak-finder/pages/UploadVideoGroupPage";
import ZoneList from "../pages/data-hub/components/liveview/components/zonelist/ZoneList";
import ZoneOverviewPage from "../pages/devices/components/ZoneOverviewPage";
import { useAuthenticatedUser } from "../common/hooks/useAuthenticatedUser";
import LoginPage from "../pages/login/LoginPage";
import UnifiedLayout from "../common/components/layout/UnifiedLayout";

const AppRoutes = () => {
  const { route, role, user } = useAuthenticatedUser();
  const location = useLocation();

  /**
   * If in the process of setting up then wait
   */
  if (route === "idle") {
    return <FullPageLoader />;
  }

  const isNotOnLoginPage = location.pathname !== RouteEnum.Login;
  const isLocal = isLocalEnvironment();

  if (!role && !user && isNotOnLoginPage) {
    if (isLocal) {
      return <Navigate to={RouteEnum.Login} replace />;
    }

    window.location.href = AuthPaths.Administration;

    return null;
  }

  const userMainLink = getRolePath(role as RoleEnum);
  const element = <Navigate to={userMainLink} replace />;

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route path={RouteEnum.Home} element={element} />

        {isLocal && <Route path={RouteEnum.Login} element={<LoginPage />} />}

        <Route element={<UnifiedLayout />}>
          <Route
            path={RouteEnum.Unauthorized}
            element={<NotAuthorizedPage />}
          />
          <Route
            path={RouteEnum.MediaViewer}
            index
            element={
              <RoleBasedRoute
                role={role}
                allowedRoles={[
                  RoleEnum.ROOT,
                  RoleEnum.CC_OPS_ADMIN,
                  RoleEnum.CC_OPS,
                  RoleEnum.CC_SALES,
                  RoleEnum.PARTNER_ADMIN,
                  RoleEnum.CUSTOMER_ADMIN,
                  RoleEnum.CUSTOMER_HSE,
                  RoleEnum.CUSTOMER_SECURITY,
                  RoleEnum.LEAKFINDER_DIRECTOR,
                  RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  RoleEnum.OPERATOR_READ_ONLY,
                  RoleEnum.PARTNER_USER,
                  RoleEnum.CUSTOMER_OPS_TEAM,
                  RoleEnum.VERIFIER,
                ]}
              >
                <MediaViewerPage />
              </RoleBasedRoute>
            }
          />
          <Route>
            <Route
              path={RouteEnum.RecentActivities}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                  ]}
                >
                  <DataHubPage />
                </RoleBasedRoute>
              }
            >
              <Route
                index
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CC_OPS_ADMIN,
                      RoleEnum.CC_OPS,
                      RoleEnum.PARTNER_ADMIN,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_ADMIN,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                    ]}
                  >
                    <DataHubDashboard />
                  </RoleBasedRoute>
                }
              />

              <Route
                path={RouteEnum.CreateZone}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CC_OPS_ADMIN,
                      RoleEnum.CC_OPS,
                      RoleEnum.CC_SALES,
                      RoleEnum.PARTNER_ADMIN,
                      RoleEnum.CUSTOMER_ADMIN,
                      RoleEnum.CUSTOMER_HSE,
                      RoleEnum.CUSTOMER_SECURITY,
                    ]}
                  >
                    <CreateZonePage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={`${RouteEnum.RecentActivities}/:model`}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CC_OPS_ADMIN,
                      RoleEnum.CC_OPS,
                      RoleEnum.PARTNER_ADMIN,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_ADMIN,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                    ]}
                  >
                    <DataHubPageContent />
                  </RoleBasedRoute>
                }
              />
            </Route>
            <Route
              path={RouteEnum.ControlRoom}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                    RoleEnum.VERIFIER,
                  ]}
                >
                  <RecentActivitiesPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.EditEventDetails}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <EditEventDetailsPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.LiveView}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                  ]}
                >
                  <DataHubLiveViewPageContent />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.ZoneList}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <ZoneList />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SensoryControlRoom}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                    RoleEnum.VERIFIER,
                  ]}
                >
                  <RecentActivitiesPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SensoryEditEventDetails}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <EditEventDetailsPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.Telemetry}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <TelemetryPage />
                </RoleBasedRoute>
              }
            >
              <Route
                path={`${RouteEnum.Telemetry}/:model`}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CC_OPS_ADMIN,
                      RoleEnum.CC_OPS,
                      RoleEnum.CC_SALES,
                      RoleEnum.PARTNER_ADMIN,
                      RoleEnum.CUSTOMER_ADMIN,
                    ]}
                  >
                    <DataHubPageContent />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={RouteEnum.TelemetryHardHat}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CC_OPS_ADMIN,
                      RoleEnum.CC_OPS,
                      RoleEnum.CC_SALES,
                      RoleEnum.PARTNER_ADMIN,
                      RoleEnum.CUSTOMER_ADMIN,
                    ]}
                  >
                    <SafetyEventsPage />
                  </RoleBasedRoute>
                }
              />
            </Route>

            <Route
              path={RouteEnum.SensoryTelemetry}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                  ]}
                >
                  <TelemetryPage />
                </RoleBasedRoute>
              }
            >
              <Route
                path={`${RouteEnum.SensoryTelemetry}/:model`}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                    ]}
                  >
                    <DataHubPageContent />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={RouteEnum.SensoryTelemetryHardHat}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                    ]}
                  >
                    <SafetyEventsPage />
                  </RoleBasedRoute>
                }
              />
            </Route>

            <Route
              path={RouteEnum.CreateNode}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,

                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <CreateNodePage />
                </RoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.Nodes}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                  ]}
                >
                  <NodesPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.ZoneSetup}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <CreateZonePage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.DevicesPageLocationId}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <DevicesPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.DevicesPageNodeId}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <DevicesPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.CreateDeviceZoneDeviceId}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <CreateZonePage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.EditZoneDeviceIdZoneId}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <EditZonePage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.ZoneOverviewPageDeviceIdZoneId}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <ZoneOverviewPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.AddModelPage}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <AddModelPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.OverviewModelPage}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <OverviewModelPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.Device}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <DeviceDetailPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.EditDevice}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <EditDevicePage />
                </RoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.Devices}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <DevicesPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.Statistics}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <StatisticsPage />
                </RoleBasedRoute>
              }
            >
              <Route path=":tabId" element={<StatisticsPage />} />
            </Route>

            <Route
              path={RouteEnum.Model}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <ModelManagerPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.DeploymentHealth}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <DeploymentHealthPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SystemHealth}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <SystemHealthPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.CustomerSettings}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <CustomerSettingsPage />
                </RoleBasedRoute>
              }
            >
              <Route path=":tabId" element={<CustomerSettingsPage />} />
            </Route>

            <Route
              path={`${RouteEnum.CustomerSettings}/${RouteEnum.CreateAlert}`}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <CreateAlertPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.CreateLocation}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <CreateLocationPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.EditLocationPageLocationId}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <EditLocationPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SystemSettings}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <SystemSettingsPage />
                </RoleBasedRoute>
              }
            >
              <Route path=":tabId" element={<SystemSettingsPage />} />
            </Route>

            <Route
              path={RouteEnum.IntegrationsAzure}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <AzureIntegrationPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.IntegrationsS3}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <S3IntegrationPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.IntegrationsEdge}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <EdgeIntegrationPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.EmailNotifications}
              element={
                <RoleBasedRoute role={role} allowedRoles={[RoleEnum.ROOT]}>
                  <EmailNotificationsPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={`${RouteEnum.TelemetryAggPlatesManagement}`}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                  ]}
                >
                  <PlatesManagement />
                </RoleBasedRoute>
              }
            />

            <Route
              path={`${RouteEnum.TelemetryAggPlatesManagementPauseAlerts}`}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <PauseAlertsPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.TelemetryAggPlatesManagementCreatePlateNumber}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <CreatePlateNumberPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SystemSettingsPlatesManagementCreatePlateNumber}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,

                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <CreatePlateNumberPageSystemSettings />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.TelemetryAggPlatesManagementEditPlateNumber}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <EditPlateNumberPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.TelemetryAggPlatesManagementEditCategory}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <EditCategoryPage />
                </RoleBasedRoute>
              }
            />
          </Route>
          <Route>
            <Route
              path={RouteEnum.Sensory}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                    RoleEnum.VERIFIER,
                  ]}
                >
                  <DataHubPage />
                </RoleBasedRoute>
              }
            >
              <Route
                path={RouteEnum.SensoryRecentActivities}
                index
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_OPS_TEAM,
                      RoleEnum.CUSTOMER_HSE,
                      RoleEnum.CUSTOMER_SECURITY,
                      RoleEnum.VERIFIER,
                    ]}
                  >
                    <DataHubDashboard />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={`${RouteEnum.SensoryRecentActivities}/:model`}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_OPS_TEAM,
                      RoleEnum.CUSTOMER_HSE,
                      RoleEnum.CUSTOMER_SECURITY,
                      RoleEnum.VERIFIER,
                    ]}
                  >
                    <DataHubPageContent />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={RouteEnum.SensoryLiveView}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                      RoleEnum.CUSTOMER_OPS_TEAM,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_HSE,
                      RoleEnum.CUSTOMER_SECURITY,
                    ]}
                  >
                    <DataHubLiveViewPageContent />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={RouteEnum.SensoryLiveViewZoneList}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CC_OPS_ADMIN,
                      RoleEnum.CC_OPS,
                      RoleEnum.CC_SALES,
                      RoleEnum.PARTNER_ADMIN,
                      RoleEnum.CUSTOMER_ADMIN,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                      RoleEnum.PARTNER_USER,
                      RoleEnum.CUSTOMER_OPS_TEAM,
                      RoleEnum.CUSTOMER_HSE,
                      RoleEnum.CUSTOMER_SECURITY,
                    ]}
                  >
                    <ZoneList />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={RouteEnum.SensoryDeploymentHealth}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                      RoleEnum.PARTNER_USER,
                    ]}
                  >
                    <DeploymentHealthPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={RouteEnum.SensorySystemHealth}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                      RoleEnum.PARTNER_USER,
                    ]}
                  >
                    <SystemHealthPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path={RouteEnum.SensoryStatistics}
                element={
                  <RoleBasedRoute
                    role={role}
                    allowedRoles={[
                      RoleEnum.ROOT,
                      RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                      RoleEnum.OPERATOR_READ_ONLY,
                      RoleEnum.PARTNER_USER,
                    ]}
                  >
                    <StatisticsPage />
                  </RoleBasedRoute>
                }
              >
                <Route path=":tabId" element={<StatisticsPage />} />
              </Route>
            </Route>

            <Route
              path={RouteEnum.SensorySystemSettings}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.CC_SALES,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <SystemSettingsPage />
                </RoleBasedRoute>
              }
            >
              <Route path=":tabId" element={<SystemSettingsPage />} />
            </Route>

            <Route
              path={`${RouteEnum.SensoryEditLocation}:locationId`}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                    RoleEnum.CC_PROCTORS,
                  ]}
                >
                  <EditLocationPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SensoryIntegrationsAzure}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                    RoleEnum.CC_PROCTORS,
                  ]}
                >
                  <AzureIntegrationPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SensoryIntegrationsS3}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                    RoleEnum.CC_PROCTORS,
                  ]}
                >
                  <S3IntegrationPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.SensoryIntegrationsEdge}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                    RoleEnum.CC_PROCTORS,
                  ]}
                >
                  <EdgeIntegrationPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={`${RouteEnum.SensoryTelemetryAggPlatesManagement}`}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <PlatesManagement />
                </RoleBasedRoute>
              }
            />

            <Route
              path={
                RouteEnum.SensoryTelemetryAggPlatesManagementCreatePlateNumber
              }
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <CreatePlateNumberPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={
                RouteEnum.SensorySystemSettingsPlatesManagementCreatePlateNumber
              }
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <CreatePlateNumberPageSystemSettings />
                </RoleBasedRoute>
              }
            />

            <Route
              path={
                RouteEnum.SensoryTelemetryAggPlatesManagementEditPlateNumber
              }
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <EditPlateNumberPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.SensoryTelemetryAggPlatesManagementEditCategory}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CC_OPS_ADMIN,
                    RoleEnum.CC_OPS,
                    RoleEnum.PARTNER_ADMIN,
                    RoleEnum.CUSTOMER_ADMIN,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                  ]}
                >
                  <EditCategoryPage />
                </RoleBasedRoute>
              }
            />

            <Route
              path={`${RouteEnum.SensorySystemSettings}/${RouteEnum.CreateAlert}`}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.PARTNER_USER,
                    RoleEnum.CUSTOMER_OPS_TEAM,
                    RoleEnum.CUSTOMER_HSE,
                    RoleEnum.CUSTOMER_SECURITY,
                  ]}
                >
                  <CreateAlertPage />
                </RoleBasedRoute>
              }
            />
          </Route>
          <Route>
            <Route
              path={RouteEnum.HumanValidator}
              element={
                <RoleBasedRoute
                  role={role}
                  allowedRoles={[RoleEnum.ROOT, RoleEnum.CC_PROCTORS]}
                >
                  <HumanValidatorPage />
                </RoleBasedRoute>
              }
            />
          </Route>
          <Route>
            <Route
              path={RouteEnum.LeakFinderClientUploads}
              element={
                <LeakFinderRoleBasedRoute
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.LEAKFINDER_DIRECTOR,
                    RoleEnum.CC_SALES,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                  role={role}
                >
                  <ClientUploadsPage />
                </LeakFinderRoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.LeakFinderClientUploadsCreate}
              element={
                <LeakFinderRoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.LEAKFINDER_DIRECTOR,
                    RoleEnum.CC_SALES,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <AddNewClientPage />
                </LeakFinderRoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.LeakFinderClientUploadsEditClient}
              element={
                <LeakFinderRoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.LEAKFINDER_DIRECTOR,
                    RoleEnum.CC_SALES,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <EditClientPage />
                </LeakFinderRoleBasedRoute>
              }
            />
            <Route
              path={RouteEnum.LeakFinderUploadNewVideoGroupPage}
              element={
                <LeakFinderRoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.LEAKFINDER_DIRECTOR,
                    RoleEnum.CC_SALES,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <UploadVideoGroupPage />
                </LeakFinderRoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.LeakFinderClientUploadsEditGroup}
              element={
                <LeakFinderRoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.LEAKFINDER_DIRECTOR,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <EditGroupPage />
                </LeakFinderRoleBasedRoute>
              }
            />

            <Route
              path={RouteEnum.LeakFinderUserPreferences}
              element={
                <LeakFinderRoleBasedRoute
                  role={role}
                  allowedRoles={[
                    RoleEnum.ROOT,
                    RoleEnum.LEAKFINDER_DIRECTOR,
                    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
                    RoleEnum.OPERATOR_READ_ONLY,
                    RoleEnum.CUSTOMER_ADMIN,
                  ]}
                >
                  <LeakFinderUserPreferencesPage />
                </LeakFinderRoleBasedRoute>
              }
            />
          </Route>
        </Route>
        <Route path={RouteEnum.Minerva} element={<MinervaPageRedirect />} />
        <Route
          path="*"
          element={<AnyRouteHandler userMainLink={userMainLink} />}
        />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
